/**
 * Commands: v1 → v2 (decision 0036, spec §2.11). Used for stored proposal bodies (plan changes, question options,
 * `approve_edited`), stored v1 command envelopes and inbound commands, and agents' `plan` / `propose_plan` /
 * `ask_human` input under the old names (decision 0037). Always 1:1, so `perChange` and `selection` indexes stay valid;
 * a v1 change with no v2 equivalent becomes `LegacyChange`, which decide always refuses.
 */
import type { FlowCommand, GraphCommand, OptionEffect } from "../commands/graph"
import type { Command, CommandEnvelope, CommandRejection, CommandResult } from "../commands/index"
import type { Capability } from "../domain/org"
import type { ProposalBody, QuestionOption } from "../domain/proposal"
import type * as V1 from "../legacy/v1/index"
import {
  fromV1EntityRef,
  fromV1Mind,
  fromV1Placement,
  fromV1RankedRef,
  fromV1Unmet,
  fromV1WorkRef,
  itemId,
} from "./common"
import { executorToAssignee, fromV1Bet, intentKeyOfId } from "./item"
import { fromV1Capability, fromV1PolicyDocument } from "./policy"
import {
  fromV1GoalSpec,
  fromV1JsonPatch,
  fromV1MemoryDraft,
  fromV1MemoryScope,
  fromV1RunOutcome,
} from "./records"

type V1Graph = V1.GraphCommand
type V1Flow = typeof V1.FlowCommand.Type
type V1Effect = typeof V1.OptionEffect.Type

/** A stored v1 command with no v2 counterpart (the intake commands): the caller answers "reload Minga". */
export class V1CommandNotMapped extends Error {
  readonly _tag = "V1CommandNotMapped"
  constructor(readonly tag: string) {
    super(`${tag} was replaced by items (CreateItem{status: triage} and plan changes): reload Minga`)
  }
}

/** The capability a v1 name became (the first of its merged names). */
const capabilityOf = (c: V1.Capability): Capability => fromV1Capability(c)[0] as Capability

/** A workstream's definition of done and supporting KPI, appended to the description (they have no item field). */
export const withDoneWhen = (
  body: string,
  definitionOfDone: ReadonlyArray<string>,
  supportsKpiId: string | null,
): string => {
  const parts = [body.trim(), `## Done when\n${definitionOfDone.map((l) => `- ${l}`).join("\n")}`]
  if (supportsKpiId !== null) parts.push(`Supports ${supportsKpiId}`)
  return parts.filter((p) => p.length > 0).join("\n\n")
}

const INITIATIVE_PATCH_UPDATE: ReadonlySet<string> = new Set(["title", "why", "body", "goalIds", "tags"])

const legacyChange = (c: V1Graph, summary: string): GraphCommand => ({
  _tag: "LegacyChange",
  v1: c as unknown as Extract<GraphCommand, { _tag: "LegacyChange" }>["v1"],
  summary,
})

const presentKeys = (patch: object): ReadonlyArray<string> =>
  Object.entries(patch)
    .filter(([, v]) => v !== undefined)
    .map(([k]) => k)

export const fromV1GraphCommand = (c: V1Graph): GraphCommand => {
  switch (c._tag) {
    case "CreateGoal": {
      const { spec, ...rest } = c
      const g = fromV1GoalSpec(spec)
      return g.target === null
        ? { ...rest, target: null, criteria: g.criteria }
        : { ...rest, target: g.target }
    }
    case "ChangeTarget":
      return {
        _tag: "ChangeTarget",
        subject: c.subject._tag === "goal" ? c.subject : { _tag: "item", id: itemId(c.subject.id) },
        condition: c.condition,
      }
    case "CreateArea": {
      const { wip, ...rest } = c
      return wip === undefined ? rest : { ...rest, wip: { maxNow: wip.maxActiveInitiatives } }
    }
    case "UpdateArea":
      return { _tag: "UpdateArea", areaId: c.areaId, patch: fromV1JsonPatch(c.patch) as never }
    case "UpdateGoal":
    case "SetGoalStatus":
    case "DefineKpi":
    case "UpdateKpi":
    case "ReviseAreaCharter":
    case "ArchiveArea":
    case "UpdateAgent":
    case "ReviseAgentCharter":
    case "SetAgentStatus":
    case "RemoveEdge":
      return c
    case "HireAgent":
    case "ChangeAgentMind":
      return { ...c, mind: fromV1Mind(c.mind) }
    case "TakeOverAgent": {
      const { mind, ...rest } = c
      return mind === undefined ? rest : { ...rest, mind: fromV1Mind(mind) }
    }
    case "CreateInitiative": {
      const shape = c.shape
      const body =
        shape._tag === "workstream"
          ? withDoneWhen(c.body ?? "", shape.definitionOfDone, shape.supportsKpiId)
          : (c.body ?? "")
      return {
        _tag: "CreateItem",
        itemId: itemId(c.initiativeId),
        type: shape._tag as never,
        title: c.title,
        why: c.why,
        ...(body.length > 0 ? { body } : {}),
        parentId: null,
        goalIds: [...c.goalIds],
        areaId: c.areaId,
        ...(c.ownerAgentId ? { owner: { _tag: "agent" as const, agentId: c.ownerAgentId } } : {}),
        assignee: null,
        status: c.horizon === "later" ? "backlog" : "todo",
        ...(shape._tag === "workstream" && shape.targetDate !== null ? { dueAt: shape.targetDate } : {}),
        ...(shape._tag === "bet" ? { bet: fromV1Bet(shape) } : {}),
        ...(c.supersedes ? { supersedes: itemId(c.supersedes) } : {}),
        ...(c.rank ? { rank: c.rank } : {}),
        intentKey: intentKeyOfId(c.initiativeId),
      }
    }
    case "UpdateInitiative": {
      const keys = presentKeys(c.patch)
      const id = itemId(c.initiativeId)
      if (keys.length > 0 && keys.every((k) => INITIATIVE_PATCH_UPDATE.has(k))) {
        const p = c.patch
        return {
          _tag: "UpdateItem",
          itemId: id,
          patch: {
            ...(p.title !== undefined ? { title: p.title } : {}),
            ...(p.why !== undefined ? { why: p.why } : {}),
            ...(p.body !== undefined ? { body: p.body } : {}),
            ...(p.goalIds !== undefined ? { goalIds: [...p.goalIds] } : {}),
            ...(p.tags !== undefined ? { tags: p.tags } : {}),
          },
        }
      }
      if (keys.length === 1 && keys[0] === "horizon" && c.patch.horizon !== undefined)
        return { _tag: "MoveItem", itemId: id, status: c.patch.horizon === "later" ? "backlog" : "todo" }
      if (keys.length === 1 && keys[0] === "ownerAgentId" && c.patch.ownerAgentId !== undefined)
        return {
          _tag: "AssignItem",
          itemId: id,
          owner: c.patch.ownerAgentId === null ? null : { _tag: "agent", agentId: c.patch.ownerAgentId },
        }
      return legacyChange(c, `UpdateInitiative ${c.initiativeId}: ${keys.join(", ") || "(empty)"}`)
    }
    case "StartInitiative":
      return { _tag: "MoveItem", itemId: itemId(c.initiativeId), status: "in_progress" }
    case "PauseInitiative":
      // a known difference (§2.11): a pending pause applies as a move to Next; nothing under it runs either way
      return { _tag: "MoveItem", itemId: itemId(c.initiativeId), status: "todo" }
    case "CloseInitiative":
      return c.as === "done"
        ? { _tag: "CloseItem", itemId: itemId(c.initiativeId), as: "done", note: c.reason }
        : {
            _tag: "CloseItem",
            itemId: itemId(c.initiativeId),
            as: "cancelled",
            resolution: { _tag: "wont_do" },
            note: c.reason,
          }
    case "CreateTask":
      return {
        _tag: "CreateItem",
        itemId: itemId(c.taskId),
        type: "task" as never,
        title: c.title,
        why: c.why,
        ...(c.body !== undefined ? { body: c.body } : {}),
        ...(c.kind !== undefined ? { kind: c.kind } : {}),
        parentId: itemId(c.parent.id),
        ...(c.areaId !== undefined ? { areaId: c.areaId } : {}),
        // an unassigned v1 task leaves the owner to decide's default (the nearest agent above)
        ...(c.assigneeId !== null ? { owner: { _tag: "agent" as const, agentId: c.assigneeId } } : {}),
        ...(c.executor !== undefined ? { assignee: executorToAssignee(c.executor) } : {}),
        checks: c.checks,
        ...(c.review !== undefined ? { review: c.review } : {}),
        ...(c.placement !== undefined ? { placement: fromV1Placement(c.placement) } : {}),
        ...(c.hands !== undefined ? { hands: c.hands } : {}),
        ...(c.tags !== undefined ? { tags: c.tags } : {}),
        ...(c.paths !== undefined ? { paths: c.paths } : {}),
        ...(c.integrations !== undefined ? { integrations: c.integrations } : {}),
        ...(c.supersedes !== undefined ? { supersedes: itemId(c.supersedes) } : {}),
        ...(c.rank !== undefined ? { rank: c.rank } : {}),
        intentKey: c.intentKey,
      }
    case "UpdateTask": {
      const { executor, placement, ...patch } = c.patch
      return {
        _tag: "UpdateItem",
        itemId: itemId(c.taskId),
        patch: {
          ...patch,
          ...(placement !== undefined ? { placement: fromV1Placement(placement) } : {}),
          ...(executor !== undefined ? { assignee: executorToAssignee(executor) } : {}),
        },
      }
    }
    case "ReviseContract":
      return {
        _tag: "ReviseChecks",
        itemId: itemId(c.taskId),
        checks: c.checks,
        ...(c.review !== undefined ? { review: c.review } : {}),
      }
    case "AssignTask":
      return {
        _tag: "AssignItem",
        itemId: itemId(c.taskId),
        owner: c.assigneeId === null ? null : { _tag: "agent", agentId: c.assigneeId },
      }
    case "CancelTask":
      return {
        _tag: "CloseItem",
        itemId: itemId(c.taskId),
        as: "cancelled",
        resolution: { _tag: "wont_do" },
        note: c.reason,
      }
    case "CompleteTask":
      return { _tag: "CloseItem", itemId: itemId(c.taskId), as: "done", note: c.note, evidence: c.evidence }
    case "DecideRequest":
      return {
        _tag: "DecideRequest",
        itemId: itemId(c.taskId),
        accept: c.accept,
        ...(c.note !== undefined ? { note: c.note } : {}),
      }
    case "Reprioritize":
      return { _tag: "Reprioritize", ref: fromV1RankedRef(c.ref), rank: c.rank }
    case "AddEdge":
      return { ...c, from: fromV1WorkRef(c.from), to: fromV1WorkRef(c.to) }
    default:
      return c satisfies never
  }
}

export const fromV1FlowCommand = (c: V1Flow): FlowCommand => {
  switch (c._tag) {
    case "RetryTask":
      return {
        _tag: "RetryItem",
        itemId: itemId(c.taskId),
        ...(c.note !== undefined ? { note: c.note } : {}),
      }
    case "UnblockTask":
      return {
        _tag: "UnblockItem",
        itemId: itemId(c.taskId),
        note: c.note,
        ...(c.hold !== undefined ? { hold: c.hold } : {}),
      }
    case "AcceptImpossible":
      return { _tag: "AcceptImpossible", itemId: itemId(c.taskId), note: c.note }
    case "PauseTask":
      return {
        _tag: "PauseItem",
        itemId: itemId(c.taskId),
        paused: c.paused,
        ...(c.note !== undefined ? { note: c.note } : {}),
      }
    default:
      return c satisfies never
  }
}

const FLOW_TAGS: ReadonlySet<string> = new Set(["RetryTask", "UnblockTask", "AcceptImpossible", "PauseTask"])

export const fromV1OptionEffect = (e: V1Effect): OptionEffect =>
  FLOW_TAGS.has(e._tag) ? fromV1FlowCommand(e as V1Flow) : fromV1GraphCommand(e as V1Graph)

export const fromV1QuestionOption = (o: typeof V1.QuestionOption.Type): QuestionOption => ({
  ...o,
  effects: o.effects.map(fromV1OptionEffect),
})

export const fromV1ProposalBody = (b: V1.ProposalBody): ProposalBody => {
  switch (b._tag) {
    case "plan_change": {
      const [first, ...rest] = b.changes
      return { ...b, changes: [fromV1GraphCommand(first), ...rest.map(fromV1GraphCommand)] }
    }
    case "question":
      return {
        ...b,
        subject: b.subject === null ? null : fromV1EntityRef(b.subject),
        options: b.options.map(fromV1QuestionOption),
      }
    case "outward_action": {
      const { taskId, ...rest } = b
      return { ...rest, itemId: itemId(taskId) }
    }
    case "permission":
      return b
    case "memory":
      return {
        ...b,
        draft: b.draft === null ? null : fromV1MemoryDraft(b.draft),
        fromScope: b.fromScope === null ? null : fromV1MemoryScope(b.fromScope),
      }
    default:
      return b satisfies never
  }
}

export const fromV1CommandRejection = (r: V1.CommandRejection): CommandRejection => {
  switch (r._tag) {
    case "PreconditionFailed":
      return { ...r, ref: fromV1EntityRef(r.ref) }
    case "Duplicate":
      return { ...r, existing: fromV1EntityRef(r.existing) }
    case "NotFound":
      return { _tag: "NotFound", ref: fromV1EntityRef(r.ref) }
    case "Invalid":
    case "Forbidden":
    case "WipLimit":
    case "StaleLease":
    case "StaleProposal":
    case "Conflict":
      return r
    default:
      return r satisfies never
  }
}

export const fromV1CommandResult = (r: V1.CommandResult): CommandResult => {
  switch (r._tag) {
    case "Rejected":
      return { _tag: "Rejected", rejection: fromV1CommandRejection(r.rejection) }
    case "Applied":
    case "Proposed":
    case "Deduplicated":
      return r
    default:
      return r satisfies never
  }
}

type V1ProjectCommand = typeof V1.ProjectCommand.Type

/** A v1 command (project or flow) as v2. Throws `V1CommandNotMapped` for the intake commands. */
export const fromV1Command = (c: V1.Command): Command => {
  if (FLOW_TAGS.has(c._tag)) return fromV1FlowCommand(c as V1Flow)
  const p = c as V1ProjectCommand
  switch (p._tag) {
    case "SubmitChangeset": {
      const [first, ...rest] = p.changes
      return { ...p, changes: [fromV1GraphCommand(first), ...rest.map(fromV1GraphCommand)] }
    }
    case "OpenProposal":
      return { ...p, body: fromV1ProposalBody(p.body), blocks: p.blocks.map(fromV1EntityRef) }
    case "ReviseProposal":
      return { ...p, body: fromV1ProposalBody(p.body) }
    case "DecideProposal":
      return p.decision._tag === "approve_edited"
        ? { ...p, decision: { _tag: "approve_edited", body: fromV1ProposalBody(p.decision.body) } }
        : { ...p, decision: p.decision }
    case "SetHold":
      return { ...p, ref: fromV1EntityRef(p.ref) }
    case "UpdateProject":
      return { _tag: "UpdateProject", patch: fromV1JsonPatch(p.patch) as never }
    case "CreatePolicy":
      return { ...p, document: fromV1PolicyDocument(p.document) }
    case "RevisePolicy":
      return { ...p, document: fromV1PolicyDocument(p.document) }
    case "UpgradePolicies":
      return { _tag: "UpgradePolicies", add: capabilityOf(p.add), like: capabilityOf(p.like) }
    case "RequestRun": {
      const { taskId, ...rest } = p
      return taskId === undefined ? rest : { ...rest, itemId: itemId(taskId) }
    }
    case "RecordRunWaiting":
      return { ...p, unmet: p.unmet.map(fromV1Unmet) }
    case "ReportOutcome":
      return { _tag: "ReportOutcome", outcome: fromV1RunOutcome(p.outcome) }
    case "RecordReview": {
      const { taskId, ...rest } = p
      return { ...rest, itemId: itemId(taskId) }
    }
    case "PostComment":
      return { ...p, target: fromV1EntityRef(p.target) }
    case "Ask":
      return { ...p, target: fromV1EntityRef(p.target) }
    case "PostArtifact": {
      const { taskId, ...rest } = p
      return { ...rest, itemId: taskId === null ? null : itemId(taskId) }
    }
    case "ProposeMemory":
      return {
        ...p,
        draft: p.draft === null ? null : fromV1MemoryDraft(p.draft),
        fromScope: p.fromScope === null ? null : fromV1MemoryScope(p.fromScope),
      }
    case "WriteMemory":
      return { ...p, draft: p.draft === null ? null : fromV1MemoryDraft(p.draft) }
    case "RequestOutwardAction": {
      const { taskId, ...rest } = p
      return { ...rest, itemId: itemId(taskId) }
    }
    case "RecordBetVerdict": {
      const { initiativeId, ...rest } = p
      return { ...rest, itemId: itemId(initiativeId) }
    }
    case "EscalateStall":
      return { ...p, subject: fromV1EntityRef(p.subject), options: p.options.map(fromV1QuestionOption) }
    case "CaptureIntake":
    case "TriageIntake":
    case "LinkIntake":
    case "CloseIntake":
      throw new V1CommandNotMapped(p._tag)
    case "UndoChangeset":
    case "RevertPlan":
    case "WithdrawProposal":
    case "ExpireProposal":
    case "GrantIntegration":
    case "RevokeIntegration":
    case "CancelRun":
    case "RecordRunLeased":
    case "RecordRunStarted":
    case "RecordRunMilestone":
    case "ReportVerdict":
    case "RaiseRunTaint":
    case "RecordRunEnded":
    case "EditComment":
    case "ResolveThread":
    case "Answer":
    case "SetArtifactState":
    case "RecordOutwardResult":
    case "RecordKpiSample":
    case "SyncWorkspace":
      return p
    case "CreateProject":
      return { ...p, lead: { ...p.lead, mind: fromV1Mind(p.lead.mind) } }
    default:
      return p satisfies never
  }
}

export const fromV1CommandEnvelope = (e: typeof V1.CommandEnvelope.Type): CommandEnvelope => {
  const { expect, command, ...rest } = e
  return {
    ...rest,
    ...(expect !== undefined
      ? { expect: expect.map((x) => ({ ref: fromV1EntityRef(x.ref), lastSeq: x.lastSeq })) }
      : {}),
    command: fromV1Command(command),
  }
}
